import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Capacitor } from '@capacitor/core';
import { Path } from '../../../../../routes/components/path';
import { APP_PAYMENT_BRIDGE_MESSAGE_TYPE } from './appPaymentWebViewBridge';

/**
 * When this page is loaded inside the payment InAppBrowser WebView (e.g. after
 * e-bank or 3DS redirect), send a handoff message so the native app closes the
 * WebView and shows this path in the main app. Used as fallback when urlChangeEvent
 * does not fire on Android.
 *
 * Used on /payment-redirect and /tilaus/tulos. The path must include the query
 * string (orderId, orderKey, status, etc.) so the opener can navigate to the correct order.
 */
export function usePaymentWebViewHandoff(): void {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const sentRef = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || sentRef.current) return;

    const queryString = searchParams?.toString();
    const path = queryString ? `${pathname}?${queryString}` : pathname;
    const isPaymentResultPath =
      path.startsWith(Path.PaymentRedirect) ||
      path.startsWith(Path.OrderStatus) ||
      path.startsWith(Path.GiftCardOrderStatus);
    if (!isPaymentResultPath) return;

    const mobileApp = (
      window as Window & { mobileApp?: { postMessage: (obj: { detail: unknown }) => void } }
    ).mobileApp;
    if (!mobileApp?.postMessage) return;

    sentRef.current = true;
    mobileApp.postMessage({
      detail: {
        type: APP_PAYMENT_BRIDGE_MESSAGE_TYPE.PAYMENT_HANDOFF,
        path,
      },
    });
  }, [pathname, searchParams]);
}
